import React from 'react';

interface TextHighlighterProps {
  text: string;
  query: string;
  className?: string;
  highlightClassName?: string;
}

/**
 * TextHighlighter component
 *
 * Highlights occurrences of the query text within the provided text content.
 * Matches are case-insensitive.
 *
 * @param text The full text content to display
 * @param query The search query string to highlight
 * @param className Optional class for the container
 * @param highlightClassName Optional class for the highlighted matches
 */
export function TextHighlighter({
  text,
  query,
  className = '',
  highlightClassName = 'bg-yellow-200 text-gray-900 border-b-2 border-yellow-400 font-bold px-0.5 rounded-sm',
}: TextHighlighterProps) {
  if (!query || !query.trim()) {
    return <span className={className}>{text}</span>;
  }

  // Highlight full query phrase only (not partial terms)
  // This prevents highlighting individual words when searching multi-word phrases
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(pattern);

  return (
    <span className={className}>
      {parts.map((part, index) => {
        // Check if this part matches the full query (case insensitive)
        const isMatch = part.toLowerCase() === query.toLowerCase();

        if (isMatch) {
          return (
            <mark key={index} className={highlightClassName}>
              {part}
            </mark>
          );
        }

        return <span key={index}>{part}</span>;
      })}
    </span>
  );
}
